import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { submit } from 'redux-form'
import NProgress from 'nprogress'

import { Modal } from 'Components/Modal'
import { ModalBody } from 'Components/Modal/ModalBody'
import { ModalFooter } from 'Components/Modal/ModalFooter'
import { ModalHeader } from 'Components/Modal/ModalHeader'
import { ModalTitle } from 'Components/Modal/ModalTitle'

import { AddConversionReportForm } from 'Containers/Form/Campaigns/AddConversionReport'
import { CONFIRM_POPUP_MESSAGE, SUCCESS_STATUS_REQUEST } from './constants'
import { getCampaigns } from 'Reducers/Campaigns/actions'
import { showReduxModal } from '../../../../Reducers/UI/actions'
import { Table, Td, Tr, Th } from '../../../../Components/Table'
import { getReadbleDateTimeString } from '../../../../shared/libraries/utils/date'
import SubmitButton from '../../../../Components/Form/SubmitButton/index'
import { splitArrayIntoChunks } from '../../../../shared/libraries/utils/array'
import { apiClient } from '../../../../Module/API/Client'

const formName = 'add-conversion-report-form'

const renderErrorModalBody = (rawErrors, csvErrors = {}) => {
  const { wrongHeaders, apiError } = csvErrors
  if (wrongHeaders) {
    return (
      <section style={{ maxHeight: 460, overflow: 'scroll' }}>
        <Table>
          <thead>
            {wrongHeaders.length === 1 ? 'Wrong Header' : 'Wrong Headers'} -
            {wrongHeaders.join(', ')}
          </thead>
        </Table>
      </section>
    )
  }

  if (apiError) {
    return (
      <section style={{ maxHeight: 460, overflow: 'scroll' }}>
        <Table>
          <thead>API Error. Check if csv is valid.</thead>
          <tbody>
            <Tr>
              <Td>{apiError}</Td>
            </Tr>
          </tbody>
        </Table>
      </section>
    )
  }

  const errors = rawErrors.map(error => {
    const {
      timestamp,
      previousTotalResults,
      metricName,
      flyerCode,
      totalResults,
    } = error
    return {
      flyerCode,
      metricName,
      message: (
        <p style={{ padding: '8px 0' }}>
          At the timestamp{' '}
          <span className="bold">{getReadbleDateTimeString(timestamp)}</span>{' '}
          there's already been{' '}
          <span className="bold">
            {previousTotalResults} {metricName}
          </span>{' '}
          conversions for flyer code <span className="bold">{flyerCode}</span>{' '}
          therefor we can't update the total to be{' '}
          <span className="bold">{totalResults}</span> which is less.
        </p>
      ),
    }
  })
  return (
    <section style={{ maxHeight: 460, overflow: 'scroll' }}>
      <Table>
        <thead>
          {Object.keys(errors[0]).map(heading => (
            <Th>{heading}</Th>
          ))}
        </thead>
        <tbody>
          {errors.map(errorData => (
            <Tr>
              {Object.values(errorData).map(item => (
                <Td>{item}</Td>
              ))}
            </Tr>
          ))}
        </tbody>
      </Table>
    </section>
  )
}

const AddConversionReportModal = props => {
  const dispatch = useDispatch()
  const [progress, setProgress] = useState(null)
  const [selectedCampaignId, setSelectedCampaignId] = useState(null)
  const [campaignReportHeaders, setCampaignReportHeaders] = useState([])
  const campaigns = useSelector(state => state.campaigns.activeCampaignsList)
  const uploadInProgress = progress && progress < 100

  const {
    modalShow,
    modalCancelButtonHandler,
    modalSubmitSuccessHandler,
    initiallySelectedCampaign,
  } = props

  useEffect(() => {
    if (modalShow) {
      dispatch(getCampaigns({ statuses: 'ACTIVE' }))
    }
    window.onbeforeunload = event => {
      if (this.state.progress && this.state.progress < 100) {
        const closeMessage = CONFIRM_POPUP_MESSAGE
        event.returnValue = closeMessage
        return closeMessage
      }
      return null
    }
  }, [])

  useEffect(() => {
    const campaign = campaigns.find(c => c.id == selectedCampaignId)
    const headers = campaign
      ? ['flyer code', ...campaign.metrics.nodes.map(metric => metric.name)]
      : null
    setCampaignReportHeaders(headers)
  }, [selectedCampaignId])

  const modalCancelHandler = () => {
    if (!progress) {
      modalCancelButtonHandler()
    }
  }

  const modalConfirmHandler = () => {
    if (!progress) {
      dispatch(submit(formName))
    }
  }

  const addConversionReport = values => {
    // Work around for now as this is not hooked up at all but we just want
    // to indicate that something is happening
    const hillFunction = x => x / (x + 3000)
    const timeStart = new Date()
    const updateProgress = () => {
      const timeElapsed = new Date() - timeStart
      setProgress(hillFunction(timeElapsed) * 100)
    }

    const interval = setInterval(updateProgress, 50)

    const callbacks = {
      onSuccess: (...args) => {
        clearInterval(interval)
        setProgress(null)
        modalSubmitSuccessHandler(...args)
      },
      onError: (errors, csvErrors) => {
        clearInterval(interval)
        setProgress(null)
        dispatch(
          showReduxModal({
            title: 'Data Conflicts',
            children: renderErrorModalBody(errors, csvErrors),
            hideCancel: true,
            confirmLabel: 'OK',
          }),
        )
      },
      stopLoading: () => {
        clearInterval(interval)
        setProgress(null)
      },
    }
    const controller = new AbortController()
    const reader = new FileReader()
    reader.addEventListener('load', async function() {
      const divider = '\r\n'
      const data = Buffer.from(this.result.split(',')[1], 'base64').toString(
        'ascii',
      )

      const dataArr = data.split(divider)
      const headers = dataArr.shift()

      const csvHeaders = headers
        .replace(/"/g, '')
        .split('\n')[0]
        .split(',')

      const wrongHeaders = csvHeaders.filter(
        field => !campaignReportHeaders.includes(field),
      )

      if (wrongHeaders.length > 0) {
        controller.abort()
        callbacks.onError([], { wrongHeaders })
        return
      }

      const chunks = splitArrayIntoChunks({
        inputArray: dataArr,
        itemsPerChunk: 200,
      })
      NProgress.start()

      let results = []

      for (let index = 0; index < chunks.length; index++) {
        const chunk = chunks[index]
        const payload = {
          report_url: '',
          report_data: [headers, ...chunk].join(divider),
        }

        const req = await apiClient.addConversionReport(
          values.campaign_id,
          payload,
        )

        const res = await req.json()

        if (req.status === SUCCESS_STATUS_REQUEST) {
          results.push(res)
        } else {
          controller.abort()
          callbacks.onError([], { apiError: res.message })
          NProgress.done()
          return
        }
      }

      const collectedData = results.reduce(
        (acc, res) => {
          acc.errors = [...acc.errors, ...res.errors]
          acc.inputs = [...acc.inputs, ...res.inputs]
          acc.results = [...acc.results, ...res.results]
          if (!res.success) {
            acc.success = res.success
          }
          return acc
        },
        { errors: [], inputs: [], results: [], success: true },
      )

      NProgress.done()

      if (collectedData.success) {
        callbacks.onSuccess(collectedData)
      } else {
        callbacks.stopLoading()
      }
      if (collectedData.errors && collectedData.errors.length > 0) {
        callbacks.onError(collectedData.errors)
      }
    })
    reader.readAsDataURL(values.report[0])
  }

  const addConversionReportFormSubmitHandler = values => {
    addConversionReport(values)
  }

  return (
    <Modal show={modalShow}>
      <ModalHeader>
        <ModalTitle>Import Conversion Report</ModalTitle>
      </ModalHeader>
      <ModalBody>
        <AddConversionReportForm
          initiallySelectedCampaign={initiallySelectedCampaign}
          campaigns={campaigns}
          form={formName}
          progress={progress}
          onSubmit={addConversionReportFormSubmitHandler}
          setSelectedCampaignId={setSelectedCampaignId}
        />
      </ModalBody>
      <ModalFooter>
        <SubmitButton
          value="cancel"
          onClick={modalCancelHandler}
          disabled={uploadInProgress}
        />
        <SubmitButton
          value={uploadInProgress ? 'Uploading...' : 'Confirm'}
          onClick={modalConfirmHandler}
          disabled={uploadInProgress}
        />
      </ModalFooter>
    </Modal>
  )
}

export default AddConversionReportModal



// WEBPACK FOOTER //
// ./src/Containers/Header/Modals/AddConversionModal/index.js